import type { TFunction } from 'i18next';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from 'src/lib/logging';
import { eAlertType } from 'src/providers/ToastProvider/Toast.enum';
import { tAlert } from 'src/providers/ToastProvider/Toast.types';
import {
  buildOutboundRefusalAlert,
  lookupOutboundRefusal,
  tOutboundRefusal,
} from 'src/utils/ErrorDirectory';
import { isWapiPlanSendBlockedError } from '../../Chat.actions.functions';
import { tChatAttachment, tDriveFile } from '../../Chat.types';
import { getFileType } from './components/Thumbnail/Thumbnail.functions';

export type tSendAttachmentsOptions = {
  chatAttachments: tChatAttachment[];
  messageBarValue: string;
  sendFileMessage: (
    file: File,
    saveFile?: boolean,
    isPreviewFile?: boolean,
    messageText?: string,
    srcAudio?: string,
  ) => Promise<unknown>;
  sendDriveFile: (file: tDriveFile, fileText?: string) => Promise<unknown>;
  callerMethod: string;
};

export type tSendAttachmentsResult = {
  failedAttachments: tChatAttachment[];
  /**
   * Set when the server refused a send (423): the attachments after it were
   * not tried, since every send from the workspace is refused the same way.
   */
  refusal?: tOutboundRefusal;
  /**
   * RET-2349 (KTD5): set when the plan guard refused a send because the plan
   * has no WhatsApp API. Nothing was sent; the attachments stay in the tray.
   */
  blockedByPlan?: boolean;
};

export async function sendAttachmentsSequentially(
  options: tSendAttachmentsOptions,
): Promise<tSendAttachmentsResult> {
  const {
    chatAttachments,
    messageBarValue,
    sendFileMessage,
    sendDriveFile,
    callerMethod,
  } = options;
  const failedAttachments: tChatAttachment[] = [];

  for (let i = 0; i < chatAttachments.length; i++) {
    const attachment = chatAttachments[i];
    let messageText = '';

    if (i === 0) {
      messageText = messageBarValue;
    }
    try {
      if (!attachment.file) {
        const driveFile = {
          mimeType: attachment.type,
          mediaType: getFileType(attachment.type),
          name: attachment.name,
          url: attachment.src,
        } as tDriveFile;

        const text = i === 0 ? messageText : '';
        await sendDriveFile(driveFile, text);
      } else {
        await sendFileMessage(
          attachment.file,
          false,
          false,
          messageText,
          attachment.src,
        );
      }
    } catch (error) {
      // A plan refusal is a decision, not a failed upload: stop, keep this
      // attachment and the rest, and let the caller show one toast.
      if (isWapiPlanSendBlockedError(error)) {
        failedAttachments.push(...chatAttachments.slice(i));
        return { failedAttachments, blockedByPlan: true };
      }

      LLog.error(
        {
          component: LOG_COMPONENTS.CHAT,
          method: callerMethod,
          operation: LOG_OPERATIONS.ERROR,
        },
        `Failed to send attachment: ${attachment.name}`,
        error as unknown as Error,
      );
      failedAttachments.push(attachment);

      // A refused send (the plan is paused, or the send could not be tied to
      // its workspace) refuses every later one the same way. Stop, keep the
      // untried attachments in the composer, and let the caller say why once.
      const refusal = lookupOutboundRefusal(error);
      if (refusal) {
        failedAttachments.push(...chatAttachments.slice(i + 1));
        return { failedAttachments, refusal };
      }
    }
  }

  return { failedAttachments };
}

/**
 * The toast after sending attachments: the refusal when the server refused the
 * send, the "some files failed, retry them" line for any other failure, and
 * nothing when every attachment went out. `t` must resolve `partialSendError`
 * (chat/chat) and declare `errors/errors` for the refusal copy.
 */
export function attachmentSendAlert(options: {
  result: tSendAttachmentsResult;
  t: TFunction;
}): tAlert | undefined {
  const { result, t } = options;

  if (result.blockedByPlan) {
    return { type: eAlertType.ERROR, message: t('wapiPlanReadOnly.message') };
  }

  if (result.refusal) {
    return buildOutboundRefusalAlert(result.refusal, t);
  }

  if (result.failedAttachments.length > 0) {
    return { type: eAlertType.ERROR, message: t('partialSendError') };
  }

  return undefined;
}
